import React from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/dialog'
import { Shield, Save, AlertCircle } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import { useUpsertRolesMutation, Role } from '@/redux/service/userManagement'
import toast from 'react-hot-toast'
import { cn } from '@/lib/utils'

interface UpsertRoleProps {
  isOpen: boolean
  onClose: () => void
  role?: Role | null
}

const UpsertRole: React.FC<UpsertRoleProps> = ({ isOpen, onClose, role }) => {
  const [upsertRole, { isLoading }] = useUpsertRolesMutation()
  const isEditMode = !!role

  const formik = useFormik({
    initialValues: {
      name: role?.name || '',
      configurations: role?.configurations === 1,
      clients: role?.clients === 1,
      attio: role?.attio === 1,
      employees: role?.employees === 1,
      timesheet: role?.timesheet === 1,
      projects: role?.projects === 1,
      datascope: role?.datascope === 1,
      finance: role?.finance === 1,
      mini_finance: role?.mini_finance === 1,
      weekly_planning: role?.weekly_planning === 1,
      admin_notes: role?.admin_notes === 1,
      vendors: role?.vendors === 1,
      widgets: role?.widgets === 1,
      edit_project_dates: role?.edit_project_dates === 1,
    },
    enableReinitialize: true,
    validationSchema: Yup.object({
      name: Yup.string()
        .required('Role name is required')
        .min(2, 'Name too short'),
      configurations: Yup.boolean(),
      clients: Yup.boolean(),
      attio: Yup.boolean(),
      employees: Yup.boolean(),
      timesheet: Yup.boolean(),
      projects: Yup.boolean(),
      datascope: Yup.boolean(),
      finance: Yup.boolean(),
      mini_finance: Yup.boolean(),
      weekly_planning: Yup.boolean(),
      admin_notes: Yup.boolean(),
      vendors: Yup.boolean(),
      widgets: Yup.boolean(),
      edit_project_dates: Yup.boolean(),
    }).test(
      'atLeastOnePermission',
      'At least one permission must be selected',
      function (values) {
        return (
          !!values.configurations ||
          !!values.clients ||
          !!values.attio ||
          !!values.employees ||
          !!values.timesheet ||
          !!values.projects ||
          !!values.datascope ||
          !!values.finance ||
          !!values.mini_finance ||
          !!values.weekly_planning ||
          !!values.admin_notes ||
          !!values.vendors ||
          !!values.widgets ||
          !!values.edit_project_dates
        )
      }
    ),
    validateOnMount: true,
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      try {
        await upsertRole({
          ...values,
          ...(role?.id && { id: role.id }),
        }).unwrap()

        toast.success(
          isEditMode ? 'Role updated successfully' : 'Role created successfully'
        )
        resetForm()
        onClose()
      } catch (err) {
        const error = err as { data?: { message?: string } }
        toast.error(error?.data?.message ?? 'Something went wrong')
      } finally {
        setSubmitting(false)
      }
    },
  })

  const permissionItems = [
    {
      id: 'configurations',
      label: 'Configurations',
      description:
        'Access company settings, team management, and CRUD operations.',
    },
    {
      id: 'attio',
      label: 'Attio',
      description: 'Access and manage Attio API integrations.',
    },
    {
      id: 'timesheet',
      label: 'Timesheet',
      description: 'View and manage company timesheet data.',
    },
    {
      id: 'clients',
      label: 'Clients',
      description: 'Visibility and management of all client pages.',
    },
    {
      id: 'employees',
      label: 'Employees',
      description: 'Access to employee directory and page.',
    },
    {
      id: 'projects',
      label: 'Projects',
      description: 'Access and management of project pages.',
    },
    {
      id: 'datascope',
      label: 'Datascope',
      description: 'Access to modify and view Looker Studio data scopes.',
    },
    {
      id: 'finance',
      label: 'Finance',
      description: 'Access to view and manage financial data/budgets.',
    },
    {
      id: 'mini_finance',
      label: 'Project Mini Finance',
      description:
        'Access to view project estimated hours and budget total amount only.',
    },
    {
      id: 'weekly_planning',
      label: 'Weekly Resource Planning',
      description:
        'Access to plan, submit, and view weekly resource allocations for team members.',
    },
    {
      id: 'admin_notes',
      label: 'Admin notes',
      description: 'Admin notes access in Project',
    },
    {
      id: 'vendors',
      label: 'Vendors',
      description:
        'Access to view and manage the vendor directory and vendor setup.',
    },
    {
      id: 'widgets',
      label: 'Widgets',
      description:
        'Access to the personal dashboard widgets page and sidebar item.',
    },
    {
      id: 'edit_project_dates',
      label: 'Edit Project Dates',
      description:
        "Edit a project's Start Date and Due Date from ClickVoltz and sync the change to ClickUp.",
    },
  ] as const

  const hasAtLeastOnePermission =
    formik.values.configurations ||
    formik.values.clients ||
    formik.values.attio ||
    formik.values.employees ||
    formik.values.timesheet ||
    formik.values.projects ||
    formik.values.datascope ||
    formik.values.finance ||
    formik.values.mini_finance ||
    formik.values.weekly_planning ||
    formik.values.admin_notes ||
    formik.values.vendors ||
    formik.values.widgets ||
    formik.values.edit_project_dates

  return (
    <Dialog open={isOpen} onOpenChange={open => !open && onClose()}>
      <DialogContent className='max-w-2xl p-0 h-[600px]  bg-background border-border rounded-xl shadow-2xl'>
        {/* HEADER */}
        <div className='px-8 py-6 border-b border-border flex items-center justify-between bg-muted/30'>
          <div className='flex items-center gap-4'>
            <div className='h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center text-primary shadow-sm'>
              <Shield className='w-6 h-6' />
            </div>
            <div className='space-y-1'>
              <DialogTitle className='text-xl font-bold text-foreground tracking-tight'>
                {isEditMode ? 'Update Role' : 'Create Role'}
              </DialogTitle>
              <DialogDescription className='text-sm text-muted-foreground'>
                {isEditMode
                  ? 'Modify account permissions and access levels'
                  : 'Define a new set of permissions for your team'}
              </DialogDescription>
            </div>
          </div>
        </div>

        <form
          onSubmit={formik.handleSubmit}
          className='flex flex-col max-h-[85vh] overflow-hidden'
        >
          <div className='p-8 space-y-8 overflow-y-auto '>
            {/* ROLE NAME */}
            <div className='space-y-2'>
              <Label
                htmlFor='name'
                className='text-xs font-bold uppercase tracking-wider text-muted-foreground px-1'
              >
                Role Identity <span className='text-red-500'>*</span>
              </Label>
              <Input
                id='name'
                name='name'
                placeholder='e.g. Administrator, Manager...'
                className={cn(
                  'h-10 border-border rounded-lg bg-background focus:ring-1 focus:ring-primary transition-all text-sm',
                  formik.touched.name &&
                    formik.errors.name &&
                    'border-destructive'
                )}
                value={formik.values.name}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                disabled={isEditMode}
              />
              {formik.touched.name && formik.errors.name && (
                <p className='text-[11px] font-medium text-destructive px-1 flex items-center gap-1'>
                  <AlertCircle className='h-3 w-3' />
                  {formik.errors.name}
                </p>
              )}
            </div>

            {/* PERMISSIONS SECTION */}
            <div className='space-y-4'>
              <div className='flex items-center justify-between px-1'>
                <div className='space-y-0.5'>
                  <h3 className='text-sm font-bold text-foreground'>
                    Access Permissions
                  </h3>
                  <p className='text-xs text-muted-foreground'>
                    Select the modules this role can access
                  </p>
                </div>
                {!hasAtLeastOnePermission && (
                  <div className='px-3 py-1 rounded-full bg-destructive/10 text-destructive text-[10px] font-bold uppercase tracking-wider flex items-center gap-1.5'>
                    <AlertCircle className='w-3 h-3' />
                    Choose at least one
                  </div>
                )}
              </div>

              <div className='grid grid-cols-1 md:grid-cols-2 gap-3'>
                {permissionItems.map(item => (
                  <div
                    key={item.id}
                    className={cn(
                      'flex flex-col justify-between p-4 rounded-xl border transition-all duration-200 gap-3 group',
                      formik.values[item.id]
                        ? 'border-primary/30 bg-primary/5 shadow-sm'
                        : 'border-border bg-muted/20 opacity-70 hover:opacity-100'
                    )}
                  >
                    <div className='flex items-start justify-between gap-4'>
                      <div className='space-y-1'>
                        <Label className='font-bold text-sm text-foreground group-hover:text-primary transition-colors cursor-pointer'>
                          {item.label}
                        </Label>
                        <p className='text-[11px] text-muted-foreground leading-relaxed'>
                          {item.description}
                        </p>
                      </div>
                      <Switch
                        checked={formik.values[item.id]}
                        onCheckedChange={checked =>
                          formik.setFieldValue(item.id, checked)
                        }
                        className='data-[state=checked]:bg-primary scale-90'
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* FOOTER */}
          <div className='px-8 py-4 border-t border-border flex gap-3 bg-muted/10 justify-end'>
            <Button
              type='button'
              variant='outline'
              onClick={onClose}
              className=' h-10 rounded-lg border-border hover:bg-muted transition-all'
            >
              Cancel
            </Button>
            <Button
              type='submit'
              disabled={
                isLoading || !formik.isValid || !hasAtLeastOnePermission
              }
              className='flex-2 h-10 rounded-lg bg-primary text-primary-foreground shadow-sm shadow-primary/10'
            >
              {isLoading ? (
                <div className='flex items-center gap-2'>
                  <div className='w-4 h-4 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin' />
                  <span>Saving...</span>
                </div>
              ) : (
                <div className='flex items-center justify-center gap-2'>
                  <Save className='w-4 h-4' />
                  <span className='font-bold'>
                    {isEditMode ? 'Update' : 'Create'} Role
                  </span>
                </div>
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}

export default UpsertRole
